/// <amd-module name="TFS/Security/Common/Permissions/SecurityPermissions" />

import * as React from "react";
import { filterXSS } from "xss";
import { Feature, PerformanceScenarios, ResourceSecurityComponent } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { PermissionTrace, PermissionValue, SubjectPermission, SubjectPermissions } from "TFS/Admin/Common/Contracts/OrganizationSettings";
import { PermissionChangeStatus, PermissionsContext } from "TFS/Admin/Common/Contracts/Permissions";
import { AccessControlEntry, AceExtendedInformation } from "TFS/Admin/Common/Contracts/Security";
import { getSecurityClient, ISecurityRestClient } from "TFS/Admin/Common/RestClient/Security";
import {
    generateRowId,
    generateRowIdDirect,
    getPermissionDisplayString,
    getPermissionId,
    PermissionsValueDisplayNames
} from "TFS/Admin/Common/Util/PermissionsHelper";
import { AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { ResourcePermission } from "TFS/Security/Common/Permissions/Contracts";
import * as Resources from "TFS/Security/Common/Permissions/Resources";
import { IObservableArray, ObservableArray } from "VSS/Core/Observable";
import { format, localeIgnoreCaseComparer } from "VSS/Core/Util/String";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { Button } from "VSSUI/Button";
import { Dropdown } from "VSSUI/Dropdown";
import { FormItem } from "VSSUI/FormItem";
import { Icon, IconSize } from "VSSUI/Icon";
import { IListBoxItem } from "VSSUI/ListBox";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Observer } from "VSSUI/Observer";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { Status, Statuses, StatusSize } from "VSSUI/Status";
import { Location } from "VSSUI/Utilities/Position";
import { getResourcesSecurityPermissions, getResourcesTracePermissions } from "./DataSource/SecurityPermissionsDataSource";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

export interface ISecurityPermissionsProps {
    subjectDescriptor: string;
    permissionsContext: PermissionsContext;
    onShowMessageCallout: (severity: MessageCardSeverity, message: string) => void;
    onLogTelemetry: (component: ResourceSecurityComponent, action: string, properties?: { [key: string]: any }) => AdminFeatureTelemetryLogger;
    clearExplicitPermissions: boolean;
    permissionsCleared: (descriptor: string) => void;
    performanceService: IVssPerformanceService;
    accountName: string;
}

export interface ISecurityPermissionsState {
    loading: boolean;
}

export class SecurityPermissions extends VssComponent<ISecurityPermissionsProps, ISecurityPermissionsState> {
    private _subjectPermissions: SubjectPermissions | undefined;
    private _permissions: IObservableArray<ResourcePermission>;
    private _updatePermissionTelemetryLogger: AdminFeatureTelemetryLogger;
    private _enableEditPipelineQueueConfigurationPermission: boolean = false;

    constructor(props: ISecurityPermissionsProps, context: IVssComponentContext) {
        super(props, context);

        this.state = { loading: true };

        if (this.props.subjectDescriptor) {
            this._getPermissions();
        }
    }

    public componentDidUpdate(prevProps: ISecurityPermissionsProps) {
        if (
            prevProps.subjectDescriptor &&
            this.props.subjectDescriptor &&
            prevProps.subjectDescriptor === this.props.subjectDescriptor &&
            !prevProps.clearExplicitPermissions &&
            this.props.clearExplicitPermissions
        ) {
            this._clearExplicitPermissions();
        } else if (prevProps.subjectDescriptor && this.props.subjectDescriptor && prevProps.subjectDescriptor !== this.props.subjectDescriptor) {
            this.setState({ loading: true });
            this._getPermissions();
        }
    }

    public render(): JSX.Element {
        if (this.state.loading) {
            return (
                <div className="flex-row flex-center justify-center flex-grow">
                    <Spinner size={SpinnerSize.large} label={Resources.Loading} />
                </div>
            );
        }

        return (
            <div>
                {this.props.subjectDescriptor && !this._subjectPermissions && this._getErrorExperience()}

                <Observer itemProvider={this._permissions}>
                    {(props: { itemProvider: ResourcePermission[] }) =>
                        this._subjectPermissions ? (
                            this._subjectPermissions.userHasReadAccess ? (
                                this._getPermissionsList(props.itemProvider, this._subjectPermissions.canEditPermissions)
                            ) : (
                                this._userDoesNotHaveReadPermissions()
                            )
                        ) : (
                            <React.Fragment />
                        )
                    }
                </Observer>
            </div>
        );
    }
 
    private _getPermissionsList(permissions: ResourcePermission[], canEditPermissions: boolean): JSX.Element[] {
        const allRows: JSX.Element[] = [];
        this._enableEditPipelineQueueConfigurationPermission = isFeatureFlagEnabled(this.context.pageContext, "Build2.EditPipelineQueueConfigurationPermission", false);
        for (const item of permissions) {
            if(!this._enableEditPipelineQueueConfigurationPermission && item.name === "Edit pipeline queue configuration") {
                continue;
            }
            allRows.push(this._renderItems(item, canEditPermissions));
        }

        return allRows;
    }

    private _renderItems(permission: ResourcePermission, canEditPermissions: boolean): JSX.Element {
        return (
            <div className="flex-cell padding-vertical-4 rhythm-horizontal-4" key={generateRowId(permission.subjectPermission)}>
                <FormItem className="flex-wrap permissions-cell-text" label={permission.subjectPermission.displayName} />
                <Dropdown
                    className="permissions-dropdown"
                    key={permission.id}
                    placeholder={permission.permission}
                    items={PermissionsValueDisplayNames}
                    onSelect={(event: React.SyntheticEvent<HTMLElement>, item: IListBoxItem<string>) =>
                        this.onSelect(event, item, permission, permission.permission)
                    }
                    disabled={!permission.subjectPermission.canEdit && !canEditPermissions}
                    renderSelectedItems={() => this._renderSelectedItem(permission.permission)}
                    ariaLabel={permission.subjectPermission.displayName}
                />
                <span className="permissions-info">
                    {permission.status
                        ? this._getPermissionChangeState(permission)
                        : this._subjectPermissions && this._subjectPermissions.userHasReadAccess && this._getInheritedPermissionInfo(permission)}
                </span>
            </div>
        );
    }

    private _renderSelectedItem(permissionValue: string): string {
        return permissionValue;
    }

    private onSelect = (
        event: React.SyntheticEvent<HTMLElement>,
        item: IListBoxItem<string>,
        permission: ResourcePermission,
        placeholder?: string
    ) => {
        const selectedItem: string | undefined = item.text;
        const selectedPermission = permission.subjectPermission;

        this._updatePermissionTelemetryLogger = this.props.onLogTelemetry(ResourceSecurityComponent.SecurityPermissions, "Updating Permission bit.");
        if (selectedPermission && this._subjectPermissions && selectedItem) {
            if (placeholder && placeholder === selectedItem) {
                // This is a No-op
                return;
            }

            const permissionId: PermissionValue = getPermissionId(selectedItem);

            this._updateChangeStatus(generateRowId(permission.subjectPermission), PermissionChangeStatus.InProgress);

            if (permissionId == PermissionValue.NotSet) {
                this._removePermission(permission, permission.subjectPermission.permissionDisplayString);
            } else {
                this._setPermission(
                    selectedPermission.namespaceId,
                    selectedPermission.token,
                    permissionId,
                    selectedPermission.bit,
                    permission.subjectPermission.permissionDisplayString
                );
            }
        }
    };

    private _getPermissionChangeState(permission: ResourcePermission): JSX.Element {
        switch (permission.status) {
            case PermissionChangeStatus.Saved:
                return <Status {...Statuses.Success} key="saved" size={StatusSize.m} className="permissions-set-status" />;

            case PermissionChangeStatus.InProgress:
                return <Status {...Statuses.Running} key="running" size={StatusSize.m} className="permissions-set-status" />;

            case PermissionChangeStatus.Error:
                return <Status {...Statuses.Failed} key="failed" size={StatusSize.m} className="permissions-set-status" />;

            case PermissionChangeStatus.None:
            default:
                break;
        }

        return <div />;
    }

    private _getInheritedPermissionInfo(permission: ResourcePermission): JSX.Element {
        if (permission.subjectPermission.isPermissionInherited || permission.subjectPermission.isPermissionGrantedBySystem) {
            return (
                <Button
                    className="permissions-inherited-info padding-4"
                    subtle
                    tooltipProps={{
                        anchorOrigin: { horizontal: Location.start, vertical: Location.end },
                        renderContent: () => this._getTooltipContent(permission.subjectPermission, permission.trace),
                        tooltipOrigin: { horizontal: Location.start, vertical: Location.start }
                    }}
                    onClick={
                        permission.subjectPermission.isPermissionGrantedBySystem
                            ? () => window.open(Resources.AzureDevOpsPermissionsLink, "_blank")
                            : undefined
                    }
                >
                    <Icon iconName="Info" size={IconSize.small} />
                </Button>
            );
        }

        return <div className="permissions-inherited-info" />;
    }

    private _getPermissionsTrace(subjectPermission: SubjectPermission) {
        // Aysnc trace and update permission
        this._updatePermissionTraceInfo(
            subjectPermission,
            generateRowId(subjectPermission),
            subjectPermission.namespaceId,
            subjectPermission.token,
            subjectPermission.bit
        );
    }

    private _getTooltipContent = (subjectPermission: SubjectPermission, trace?: PermissionTrace): JSX.Element => {
        if (subjectPermission.isPermissionGrantedBySystem) {
            return (
                <div aria-live="polite">
                    <p className="inheritance-type-description">{Resources.SystemStorePermissionInfo}</p>
                </div>
            );
        }

        if (!trace) {
            this._getPermissionsTrace(subjectPermission);
            // Use aria-live so the content is announced when it is loaded.
            return <div aria-live="polite">{Resources.Loading}</div>;
        }

        if (trace.error) {
            return <div aria-live="polite">{trace.error}</div>;
        }

        const tokenValue = filterXSS(trace.tokenDisplayName);
        return (
            <div aria-live="polite">
                <p className="inheritance-type-description">{format(Resources.GroupInheritance, tokenValue)}</p>
                {trace.affectingGroups.map((group, i) => (
                    <p key={i} className="inheritance-group-name">
                        {filterXSS(group.group.displayName)}
                    </p>
                ))}
            </div>
        );
    };

    private async _removePermission(permission: ResourcePermission, currPermissionsDisplayString: string) {
        if (!this._subjectPermissions) {
            return;
        }

        const selectedPermission = permission.subjectPermission;
        try {
            const securityClient: ISecurityRestClient = getSecurityClient(this.context.pageContext);

            await securityClient.removePermission(
                selectedPermission.namespaceId,
                this._subjectPermissions.identityDescriptor.toString(),
                selectedPermission.bit,
                selectedPermission.token
            );

            // Nothing about the data returned from removePermissions tells us whether "not set" results in allow inherited or deny inherited, etc.
            // So we have no choice but to fetch the set again and see what we have.
            const permissions: SubjectPermissions | undefined = await getResourcesSecurityPermissions(
                this.context.pageContext,
                this.props.subjectDescriptor,
                this.props.permissionsContext.permissionSetId,
                this.props.permissionsContext.token,
                this.props.accountName
            );

            if (!permissions) {
                this._updateChangeStatus(generateRowId(permission.subjectPermission), PermissionChangeStatus.Error);
                this._updatePermissionTelemetryLogger.captureError(
                    "RemovePermission: Error retrieving security permissions after remove. Check product trace."
                );
                return;
            }

            const itemIndex = this._subjectPermissions.subjectPermissions.findIndex((x) => generateRowId(x) == generateRowId(selectedPermission));
            if (itemIndex > -1) {
                const permission = permissions.subjectPermissions[itemIndex];

                this._updatePermission(
                    selectedPermission.namespaceId,
                    selectedPermission.bit,
                    permission.effectivePermissionValue,
                    currPermissionsDisplayString,
                    permission.isPermissionInherited,
                    permission.isPermissionGrantedBySystem
                );
            }
        } catch (error) {
            this._updateChangeStatus(generateRowId(permission.subjectPermission), PermissionChangeStatus.Error);
            this._updatePermissionTelemetryLogger.captureError("Remove permission bit failed: " + error.message || String(error));
        }
    }

    private async _setPermission(
        namespaceId: string,
        token: string,
        permissionValue: PermissionValue,
        permissionBit: number,
        currPermissionsDisplayString: string
    ) {
        if (!this._subjectPermissions) {
            return;
        }

        const securityClient: ISecurityRestClient = getSecurityClient(this.context.pageContext);

        let allow = 0;
        let deny = 0;

        if (permissionValue == PermissionValue.Allow) {
            allow = permissionBit;
        } else if (permissionValue == PermissionValue.Deny) {
            deny = permissionBit;
        }

        const aceExtendedInfo: AceExtendedInformation = {
            effectiveAllow: allow,
            effectiveDeny: deny,
            inheritedAllow: allow,
            inheritedDeny: deny
        };

        const setAccessControlEntriesItem: AccessControlEntry = {
            descriptor: this._subjectPermissions.identityDescriptor,
            allow: allow,
            deny: deny,
            extendedInfo: aceExtendedInfo
        };

        const container = {
            token: token,
            merge: true,
            accessControlEntries: [setAccessControlEntriesItem]
        };

        try {
            await securityClient.setAccessControlEntries(container, namespaceId);

            this._updatePermission(namespaceId, permissionBit, permissionValue, currPermissionsDisplayString, undefined, undefined);
        } catch (error) {
            this._updateChangeStatus(generateRowIdDirect(namespaceId, permissionBit), PermissionChangeStatus.Error);
            this._updatePermissionTelemetryLogger.captureError("set permission failed: " + error.message || String(error));
        }
    }

    private _updatePermission(
        namespaceId: string,
        permissionBit: number,
        newValue: PermissionValue,
        currPermissionsDisplayString: string,
        newPermissionInherited?: boolean,
        newPermissionGranted?: boolean
    ) {
        const rowId = generateRowIdDirect(namespaceId, permissionBit);
        if (this._subjectPermissions) {
            const itemIndex = this._subjectPermissions.subjectPermissions.findIndex((x) => generateRowId(x) == rowId);

            if (itemIndex > -1) {
                // Update subject permissions
                this._subjectPermissions.subjectPermissions[itemIndex].effectivePermissionValue = newValue;
                this._subjectPermissions.subjectPermissions[itemIndex].permissionDisplayString = getPermissionDisplayString(newValue);

                this._subjectPermissions.subjectPermissions[itemIndex].isPermissionInherited =
                    newPermissionInherited == undefined ? false : newPermissionInherited;
                this._subjectPermissions.subjectPermissions[itemIndex].isPermissionGrantedBySystem =
                    newPermissionGranted == undefined ? false : newPermissionGranted;

                // Update permission
                const subjectPermission = this._subjectPermissions.subjectPermissions[itemIndex];

                const permissionIndex = this._permissions.value.findIndex((x) => x.id == rowId);

                if (permissionIndex < 0) {
                    this._updatePermissionTelemetryLogger.captureError("updatePermission: could not find the permissions index to update.");
                    return;
                }

                this._getPermissionsTrace(subjectPermission);
                this._permissions.change(permissionIndex, {
                    id: rowId,
                    name: subjectPermission.displayName,
                    permission: subjectPermission.permissionDisplayString,
                    status:
                        localeIgnoreCaseComparer(subjectPermission.permissionDisplayString, currPermissionsDisplayString) == 0
                            ? PermissionChangeStatus.None
                            : PermissionChangeStatus.Saved,
                    subjectPermission: subjectPermission
                } as ResourcePermission);
            }
        }
    }

    private _updatePermissionTraceInfo = async (
        subjectPermission: SubjectPermission,
        rowId: string,
        namespaceId: string,
        token: string,
        permissionBit: number,
        telemetryLogger?: AdminFeatureTelemetryLogger
    ) => {
        // Trace permission

        if (!telemetryLogger) {
            telemetryLogger = this.props.onLogTelemetry(ResourceSecurityComponent.SecurityPermissions, "Trace permission");
        }

        if (subjectPermission.isPermissionInherited) {
            const trace = await getResourcesTracePermissions(
                this.context.pageContext,
                this.props.subjectDescriptor,
                this.props.permissionsContext.permissionSetId,
                this.props.permissionsContext.token,
                namespaceId,
                token,
                permissionBit.toString()
            );

            if (!trace) {
                telemetryLogger.captureError("traceAndUpdatePermission: Trace permission failed. Check product trace.");
                return;
            }

            // Update permission
            const permissionIndex = this._permissions.value.findIndex((x) => x.id == rowId);
            if (permissionIndex > -1) {
                const permission = this._permissions.value[permissionIndex];
                permission.trace = trace;
                this._permissions.change(permissionIndex, permission);
            } else {
                telemetryLogger.captureError("traceAndUpdatePermission: Cannot find permissions index to trace.");
            }
        }
    };

    private _getErrorExperience(): JSX.Element {
        return (
            <div className="empty-member flex-container">
                <div className="empty-permissions-heading">{Resources.EmptyPermissionsHeading}</div>
                <div className="empty-permissions-body">{Resources.EmptyPermissionsBody}</div>
            </div>
        );
    }

    private _userDoesNotHaveReadPermissions(): JSX.Element {
        return (
            <MessageCard className="permissions-card" severity={MessageCardSeverity.Error}>
                {Resources.PermissionsInsufficientReadPermissions}
            </MessageCard>
        );
    }

    private _updateChangeStatus(rowId: string, status: PermissionChangeStatus) {
        const permissionIndex = this._permissions.value.findIndex((x) => x.id == rowId);

        if (permissionIndex > -1) {
            const permission = this._permissions.value[permissionIndex];
            permission.status = status;

            this._permissions.change(permissionIndex, permission);
        } else {
            this._updatePermissionTelemetryLogger.captureError(
                "UpdateChangeStatus: Cannot find permissions index to update. PermissionChangeStatus: " + status.toString()
            );
        }
    }

    private _getPermissions = async () => {
        const telemetryLogger = this.props.onLogTelemetry(
            ResourceSecurityComponent.SecurityPermissions,
            "Loading permissions for the selected member."
        );
        this.props.performanceService.startScenario(PerformanceScenarios.LoadSecurityPermissionsData, false);

        const permissions = await getResourcesSecurityPermissions(
            this.context.pageContext,
            this.props.subjectDescriptor,
            this.props.permissionsContext.permissionSetId,
            this.props.permissionsContext.token,
            this.props.accountName
        );

        this._subjectPermissions = permissions;
        this._permissions = new ObservableArray<ResourcePermission>([]);

        if (this._subjectPermissions) {
            this._subjectPermissions!.subjectPermissions.forEach((element) => {
                // Aysnc trace and update permission
                const permission = {
                    id: generateRowId(element),
                    name: element.displayName,
                    permission: element.permissionDisplayString,
                    status: PermissionChangeStatus.None,
                    subjectPermission: element
                } as ResourcePermission;
                this._permissions.push(permission);
                this._getPermissionsTrace(permission.subjectPermission);
            });
        } else {
            telemetryLogger.captureError("getPermissions: Data provider load failed. Check product trace.");
        }

        this.setState({ loading: false });

        this.props.performanceService.endScenario(Feature.ResourceSecurity, PerformanceScenarios.LoadSecurityPermissionsData, false);
    };

    private async _clearExplicitPermissions() {
        const telemetryLogger = this.props.onLogTelemetry(ResourceSecurityComponent.SecurityPermissions, "Clear explicit permissions.");
        const subjectDescriptor = this.props.subjectDescriptor;
        try {
            if (!this._subjectPermissions) {
                telemetryLogger.captureError("No permission found.");
                return;
            }

            const securityClient: ISecurityRestClient = getSecurityClient(this.context.pageContext);
            const { permissionSetId } = this.props.permissionsContext;

            // Token may have been converted server-side into its canonical form, so use it instead of the provided one.
            const token = this._subjectPermissions.subjectPermissions?.[0]?.token || this.props.permissionsContext.token;

            await securityClient.removeAccessControlEntries(permissionSetId, token, this._subjectPermissions.identityDescriptor.toString());
            this.props.permissionsCleared(subjectDescriptor);
        } catch (error) {
            telemetryLogger.captureError("Failed to clear permissions.");
        }
    }
}

VssComponent.register("ms.vss-admin-web.security-permissions-common", SecurityPermissions);
